import React, {useEffect, useMemo, useState} from 'react';
import {
  Grid,
  Box,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
} from '@mui/material';
import {useField} from 'formik';
import {useQuery} from '@apollo/client';
import AutoField from '../../../components/AutoForm/AutoFieldNew';
import {Panel} from '../../../components/Panel/Panel';
import {useMandateStyles} from './MandatesPage/mandatesStyles';
import {atrRiskValue, BuilderMandates} from './BuilderMandates';
import {LATEST_ATR} from '../../../components/AttitudeToRisk/AtrQueries';
import {LatestAtr, LatestAtrVariables} from '../../../schema-types/LatestAtr';
import {GetModelPortfolios_modelPortfolios} from '../../../schema-types/GetModelPortfolios';
import usePlanNamesProviders, {
  PlanProviderSelectOption,
} from '../../../hooks/usePlanNamesProviders';
import {useAccountContext} from '../../../contexts/AccountContext';
import {modelObjectField, modelTextField} from '../CreateBuilder/builderForm';
import {hasWrapper} from '../CreateBuilder/BuilderSourceDestination/builderUtils';
import {ValidationObjectType} from '../CreateBuilder/BuilderSourceDestination/builderValidators';
import {BuilderPlanForm} from '../CreateBuilder/BuilderSourceDestination/BuilderPlanForm';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import type {modelPortfolioField} from '../BuilderModify/builderModifyFieldTypes';
import {PotDetails} from '../../../schema-types/PotDetails';
import {BusinessUnitEnum} from '../../../schema-types/globalTypes';
import {useAutoForm, useAutoField} from '../../../components/AutoForm/useAutoForm';
import {ScrollableModal} from '../../../components/Common/ScrollableModal';

import {UPDATES} from '../../../shared/UPDATES';
import {useReviewContext} from '../../../contexts/ReviewContext';
import validateUUID from '../../../utils/validateUUID';

export const useShowMandateSelector = ({
  formikKeyPrefix,
  planProviderOptions,
  sourcePot,
}: {
  planProviderOptions?: PlanProviderSelectOption[];
  formikKeyPrefix: string;
  sourcePot: PotDetails | undefined;
}) => {
  const [mandateField, mandateMeta, mandateHelpers] = useField(
    `${formikKeyPrefix}.model`
  );
  const [planProvider, planProviderMeta, planProviderHelpers] = useField(
    `${formikKeyPrefix}.planProvider`
  );
  const showMandateSelector = useMemo(() => {
    if (planProviderOptions?.length) {
      const selectedProductProvider = planProviderOptions.find(
        (x) => x.value === planProvider.value
      );
      if (selectedProductProvider) {
        return selectedProductProvider.showMandateSelector;
      }
    } else if (sourcePot) {
      return sourcePot.productProvider?.showMandateSelector;
    }
  }, [planProvider.value, planProviderOptions, mandateField.value, sourcePot]);
  return {showMandateSelector};
};

export const BuilderMandatesWrapper: React.FC<{
  formikKeyPrefix: string;
  planProviderOptions?: PlanProviderSelectOption[];
  disabledReason?: string;
  showCurrentMandate: boolean;
  sourcePot?: PotDetails | undefined;
  isChild?: boolean;
}> = ({
  formikKeyPrefix,
  planProviderOptions,
  showCurrentMandate,
  sourcePot,
  disabledReason: disableEdit,
  isChild = false,
}) => {
  const {values, errors: reccsErrors} = useAutoForm<builderFieldsType>();
  const {accountId, account} = useAccountContext();
  const [open, setOpen] = React.useState(false);

  const {showMandateSelector} = useShowMandateSelector({
    formikKeyPrefix,
    planProviderOptions,
    sourcePot,
  });

  const [planProvider, planProviderMeta, planProviderHelpers] = useAutoField(
    `${formikKeyPrefix}.planProvider`
  );
  const [mandateField, mandateMeta, mandateHelpers] = useAutoField(
    `${formikKeyPrefix}.model`
  );
  const {data: atrData} = useQuery<LatestAtr, LatestAtrVariables>(LATEST_ATR, {
    variables: {
      accountId: validateUUID(accountId)!,
      contactId: validateUUID(values.contact)!,
    },
    skip: !validateUUID(accountId) || !validateUUID(values.contact),
  });
  const handleOpen = () => {
    if (disableEdit) {
      return;
    }
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  return (
    <>
      {showCurrentMandate ? (
        <Box pb={1} display="flex" gap={2} alignContent="center" alignItems="center">
          <Typography>
            Current mandate:{' '}
            <strong>
              {sourcePot?.mandate ||
                sourcePot?.modelPortfolio?.name ||
                (sourcePot ? 'No mandate' : 'No plan selected')}
            </strong>
          </Typography>
        </Box>
      ) : null}
      <>
        {showMandateSelector ? (
          <Grid className="mandate-select" item xs={12} onClickCapture={handleOpen}>
            <AutoField
              field={{
                ...modelObjectField,
                disabled: !!disableEdit,
                label: showCurrentMandate ? 'Select new mandate' : modelObjectField.label,
                id: `${formikKeyPrefix}.model`,
              }}
            />
          </Grid>
        ) : (
          <Grid item xs={12}>
            <AutoField
              className="mandate-type"
              field={{
                ...modelTextField,
                disabled: !!disableEdit,
                label: showCurrentMandate ? 'New mandate' : modelTextField.label,
                id: `${formikKeyPrefix}.model`,
              }}
            />
          </Grid>
        )}
      </>

      <ScrollableModal size="xlarge" open={open} onClose={handleClose}>
        <BuilderMandatesModal
          pot={sourcePot}
          open={open}
          handleClose={handleClose}
          formikKeyPrefix={formikKeyPrefix}
          hasBondParent={values.mainPlanType === 'bond' && isChild}
          latestATR={atrData || null}
        />
      </ScrollableModal>
    </>
  );
};

export const BuilderMandatesModal: React.FC<{
  open: boolean;
  handleClose: () => void;
  formikKeyPrefix: string;
  pot?: PotDetails;
  hasBondParent: boolean;
  latestATR: LatestAtr | null;
}> = ({handleClose, open, latestATR: atrData, formikKeyPrefix, pot, hasBondParent}) => {
  const {
    values,
    errors: reccsErrors,
    getFieldMeta,
    setFieldValue,
  } = useAutoForm<builderFieldsType>();
  const [modelTemp] = useAutoField<{id: string} | undefined>(
    `${formikKeyPrefix}.modelTemp`
  );
  const {accountType} = useAccountContext();
  const [hoverBannerName, setHoverBannerName] = useState('');
  const [selectedBannerName, setSelectedBannerName] = useState('');
  const [showPanel, setShowPanel] = useState(false);
  const [selectedPortfolios, setSelectedPortfolios] = useState<
    GetModelPortfolios_modelPortfolios[] | null
  >([]);
  const [hoveredPortfolios, setHoveredPortfolios] = useState<
    GetModelPortfolios_modelPortfolios[] | null
  >([]);
  const isSubplan = hasWrapper(values.mainPlanType);
  const {names: productNames, queryGetProducts} = usePlanNamesProviders();
  const formikKeyPrefixParent = isSubplan
    ? `${formikKeyPrefix?.split('.subplan')?.[0]}.parent`
    : '';

  const classes = useMandateStyles();
  const errorsBeforeStarting = useMemo(() => {
    const errors: React.ReactNode[] = [];
    if (!values.contact) {
      errors.push(<div>Please select a contact</div>);
    }
    if (!pot) {
      if (isSubplan) {
        if (!getFieldMeta(`${formikKeyPrefixParent}.planProvider`).value) {
          errors.push(<div>Parent plan provider must be selected</div>);
        }
        if (
          !getFieldMeta(`${formikKeyPrefixParent}.planType`).value &&
          !getFieldMeta(`${formikKeyPrefixParent}.productNameId`).value
        ) {
          errors.push(<div>A parent plan must be selected</div>);
        }
        if (!getFieldMeta(`${formikKeyPrefix}.planProvider`).value) {
          errors.push(<div>Subplan provider must be selected</div>);
        }
      } else {
        if (!getFieldMeta(`${formikKeyPrefix}.planProvider`).value) {
          errors.push(<div>Plan provider must be selected</div>);
        }
        if (
          !getFieldMeta(`${formikKeyPrefix}.planType`).value &&
          !getFieldMeta(`${formikKeyPrefix}.productNameId`).value
        ) {
          errors.push(<div>A plan must be selected</div>);
        }
      }
    }
    return errors;
  }, [
    values.contact,
    getFieldMeta(`${formikKeyPrefix}.planProvider`).value,
    getFieldMeta(`${formikKeyPrefix}.planType`).value,
    getFieldMeta(`${formikKeyPrefix}.productNameId`).value,
    getFieldMeta(`${formikKeyPrefixParent}.planProvider`).value,
    getFieldMeta(`${formikKeyPrefixParent}.planType`).value,
    isSubplan,
  ]);
  const [validation, setValidation] = useState<ValidationObjectType>({
    invalidFields: 0,
    totalFields: 0,
  });
  useEffect(() => {
    setValidation({
      totalFields: modelTemp.value ? 1 : 0,
      invalidFields: 0,
    });
  }, [modelTemp.value]);
  const displayedPortfolios = hoveredPortfolios || selectedPortfolios;
  const bannerName = hoverBannerName || selectedBannerName;
  const [productName, setProductName] = useState<string | undefined>(
    (getFieldMeta(`${formikKeyPrefix}.planType`).value ||
      getFieldMeta(`${formikKeyPrefix}.productNameId`).value) as string | undefined
  );

  useEffect(() => {
    if (!isSubplan) {
      return;
    }
    queryGetProducts({
      variables: {
        productType: 'sub',
        providerId: getFieldMeta(`${formikKeyPrefix}.planProvider`).value as string,
      },
    });
  }, [isSubplan, productName, getFieldMeta(`${formikKeyPrefix}.planProvider`).value]);

  useEffect(() => {
    if (!productNames?.products?.length) {
      return;
    }
    setProductName(productNames.products?.[0].id);
  }, [productNames]);

  return (
    <Panel sx={classes.panel} title="Mandates">
      <Grid container spacing={2}>
        {errorsBeforeStarting.length ? (
          <Box>
            <Box mt={2}>
              <Typography style={{fontWeight: 600}} variant="h6">
                You cannot select a mandate without the following:
              </Typography>
            </Box>
            <Box mt={1}>{errorsBeforeStarting}</Box>
          </Box>
        ) : (
          <>
            <Grid item xs={12} md={8}>
              <BuilderMandates
                pot={pot}
                setHoverBannerName={setHoverBannerName}
                setSelectedBannerName={setSelectedBannerName}
                initialRiskValue={null}
                atr={atrData?.latestAtr?.finalValue as atrRiskValue}
                formikKeyPrefix={formikKeyPrefix}
                planProvider={
                  getFieldMeta(`${formikKeyPrefix}.planProvider`).value as string
                }
                productName={productName}
                showPanel={showPanel}
                modelPortfolio={
                  getFieldMeta(`${formikKeyPrefix}.model`).value as modelPortfolioField
                }
                setHoveredPortfolios={setHoveredPortfolios}
                setSelectedPortfolios={setSelectedPortfolios}
                hasBondParent={hasBondParent}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              {accountType === BusinessUnitEnum.DA ? null : (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showPanel}
                      onChange={() =>
                        setShowPanel((previousShowPanel) => !previousShowPanel)
                      }
                    />
                  }
                  label="Show Panel"
                />
              )}

              <BuilderPlanForm
                reccId=""
                validation={validation}
                summary={
                  getFieldMeta(`${formikKeyPrefix}.modelTemp`).value ? (
                    <Box>
                      <Typography>
                        Selected mandate:
                        <br />{' '}
                        <Typography style={{fontSize: 22}}>
                          {
                            (getFieldMeta(`${formikKeyPrefix}.modelTemp`).value as any)
                              ?.name
                          }
                        </Typography>
                      </Typography>
                    </Box>
                  ) : (
                    <Typography>
                      <strong>Please select a mandate</strong>
                    </Typography>
                  )
                }
                isSelected
                isSubplan={false}
                handleChange={() => {}}
              />
              <Box mt={1} />
              {displayedPortfolios?.length ? (
                <BuilderPlanForm
                  sameReview={false}
                  reccId=""
                  validation={{
                    totalFields: 0,
                    invalidFields: 0,
                  }}
                  summary={
                    <Typography>
                      Currently viewing:
                      <br />
                      <strong>{bannerName}</strong>
                    </Typography>
                  }
                  isSelected
                  isSubplan={false}
                  handleChange={() => {}}
                  details={
                    <Box>
                      {displayedPortfolios?.length === 0 && (
                        <Typography>No mandates</Typography>
                      )}
                      <RadioGroup
                        value={getFieldMeta(`${formikKeyPrefix}.modelTemp`).value}
                        onChange={(event) => {
                          const id = event?.target.value;
                          if (!id) {
                            return;
                          }
                          const model = displayedPortfolios?.find((x) => x?.id === id);

                          setFieldValue(
                            UPDATES._1606,
                            `${formikKeyPrefix}.modelTemp`,
                            {
                              id: model?.id,
                              name: model?.name,
                              providerContactLastName: model?.providerContact?.lastName,
                              providerContactJobTitle: model?.providerContact?.jobTitle,
                            },
                            true
                          );
                        }}
                      >
                        {(displayedPortfolios ?? [])
                          .sort((a, b) => (a.name?.length ?? 0) - (b.name?.length ?? 0))
                          .map((x, i) => {
                            if (i === 0 && !modelTemp.value?.id) {
                              setFieldValue(
                                UPDATES._1606,
                                `${formikKeyPrefix}.modelTemp`,
                                {
                                  id: x?.id,
                                  name: x?.name,
                                  providerContactLastName: x?.providerContact?.lastName,
                                  providerContactJobTitle: x?.providerContact?.jobTitle,
                                },
                                true
                              );
                            }
                            return (
                              <Box key={x?.id} mb={2}>
                                <FormControlLabel
                                  checked={
                                    x?.id ===
                                    (
                                      getFieldMeta(`${formikKeyPrefix}.modelTemp`)
                                        ?.value as any
                                    )?.id
                                  }
                                  label={x?.name}
                                  value={x?.id}
                                  control={<Radio />}
                                />
                              </Box>
                            );
                          })}
                      </RadioGroup>
                    </Box>
                  }
                />
              ) : null}
            </Grid>
          </>
        )}
      </Grid>
      <Box width="100%">
        <Box sx={classes.justification} />
        <Box sx={classes.submit}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              setFieldValue(
                UPDATES._1606,
                `${formikKeyPrefix}.modelTemp`,
                undefined,
                true
              );
              handleClose();
            }}
            sx={classes.cancel}
          >
            Cancel
          </Button>
          <Button
            disabled={!getFieldMeta(`${formikKeyPrefix}.modelTemp`).value}
            variant="contained"
            color="primary"
            onClick={() => {
              setFieldValue(
                UPDATES._1607,
                `${formikKeyPrefix}.model`,
                getFieldMeta(`${formikKeyPrefix}.modelTemp`).value as any,
                true
              );
              // Mandate determines which DFM/FP/product fees are valid; clear stale
              // fee IDs so the picker re-evaluates against the new mandate's options.
              // Load-bearing: BuilderPlanFees' auto-select effect only re-populates the
              // fee when this field is empty, so do not remove this clear (ON-9247).
              setFieldValue(
                UPDATES._1608,
                `${formikKeyPrefix}.planFees.DFMFeeId`,
                null,
                true
              );
              setFieldValue(
                UPDATES._1608,
                `${formikKeyPrefix}.planFees.FPFeeId`,
                null,
                true
              );
              setFieldValue(
                UPDATES._1608,
                `${formikKeyPrefix}.planFees.productFeeId`,
                null,
                true
              );
              handleClose();
            }}
          >
            Submit Mandate
          </Button>
        </Box>
      </Box>
    </Panel>
  );
};
